import {Link} from 'react-router-dom';
import Icon from '../Icon';
import KindPill from './KindPill';
import KindTile from './KindTile';
import ProgressBar from '../feedback/ProgressBar';
import {kindInfo} from '../../lib/kinds';
import {useUploadState} from '../../lib/uploadQueue';
import {formatTime} from '../../lib/when';
import {Post} from '../../types';

// The Journal card (DESIGN.md §5). With a photo: 196px cover carrying the kind pill on its
// top-left corner (seen while scrolling, so a problem's rust pill stands out), then the title
// and the quiet meta line. Without: the same card as a row with a 36px kind tile. A tell that
// "makes me happy" carries the smile in marigold: on the photo's other corner, or by the title.

const HappyTile = ({className = ''}: {className?: string}) => (
    <span title="Makes me happy" className={`flex h-[26px] w-[26px] items-center justify-center rounded-pill bg-marigold-soft text-marigold-text ${className}`}>
        <Icon name="happy" size={18} />
        <span className="sr-only">Makes me happy</span>
    </span>
);
export const metaLine = (post: Post): string =>
    [post.detail, post.plants.map(t => t.label).join(', '), post.place?.name, formatTime(post.whenLocal)].filter(Boolean).join(' · ');

export default function TellCard({post}: {post: Post}) {
    const title = post.title || kindInfo(post.kind).label;
    const queued = useUploadState(post.id);
    const upload = queued && queued.total > 0 ? {done: queued.done, total: queued.total, failed: queued.failed, previews: queued.photos.map(p => p.previewUrl)} : null;
    const cover = post.photos[0]?.url ?? upload?.previews[0] ?? null;

    return (
        <Link to={`/tell/${post.id}`} className="card settle-in block overflow-hidden no-underline">
            {cover ? (
                <div className="relative h-[196px] bg-sunken">
                    <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover" />
                    <KindPill kind={post.kind} className="absolute left-[10px] top-[10px] shadow-1" />
                    {post.happy && <HappyTile className="absolute right-[10px] top-[10px] shadow-1" />}
                    {post.photos.length > 1 && (
                        <span className="absolute bottom-3 right-3 rounded-[6px] bg-scrim/50 px-2 py-[2px] text-xs font-semibold text-white">{post.photos.length} photos</span>
                    )}
                    {upload && !upload.failed && upload.done < upload.total && (
                        <div className="absolute inset-x-0 bottom-0 bg-scrim/40 px-3 py-2 text-xs font-semibold text-white">
                            Uploading {upload.done + 1} of {upload.total}…
                            <ProgressBar value={upload.done / upload.total} className="mt-1 bg-white/30" />
                        </div>
                    )}
                    {upload?.failed && <div className="absolute inset-x-0 bottom-0 bg-rust/90 px-3 py-2 text-xs font-semibold text-white">A photo didn&rsquo;t upload. Open the tell to retry.</div>}
                </div>
            ) : null}
            <div className={`flex gap-3 ${cover ? 'flex-col px-4 pb-4 pt-[14px]' : 'items-start px-4 py-[14px]'}`}>
                {!cover && <KindTile kind={post.kind} size={36} className="mt-[2px]" />}
                <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                        <h3 className="m-0 min-w-0 flex-1 font-display text-lg font-[650] leading-[1.2] tracking-[-0.01em]">{title}</h3>
                        {post.happy && !cover && <HappyTile className="shrink-0" />}
                    </div>
                    {(!cover || metaLine(post)) && (
                        <div className="meta mt-2 truncate">{cover ? metaLine(post) : `${kindInfo(post.kind).label}${metaLine(post) ? ` · ${metaLine(post)}` : ''}`}</div>
                    )}
                </div>
            </div>
        </Link>
    );
}
